import { Query, QueryResults, RawEventRow, VizType } from '@kentik/ui-shared/query/query.types';
import uuid from 'uuid';
import { action, computed, observable, ObservableMap } from 'mobx';
import { DEFAULT_QUALITATIVE_PALETTES } from 'app/stores/colors/Colors';
import { merge } from 'lodash';
import { Client } from 'core/util/sseConnect';
import { getRollupKey, sortResultRows, DEFAULT_VIZ_LIMIT } from '@kentik/ui-shared/query/query';
import UdeQueryResultsCollection from './UdeQueryResultsCollection';
import RawEventCollection from './RawEventCollection';

type QueryStatus = 'init' | 'running' | 'partial' | 'done' | 'error';

/**
 * Optional function to transform the query before execution.
 * This allows the caller to provide query transformations (e.g., filter transforms)
 * without QueryModel needing to know about specific stores or data sources.
 * Can be async to allow for data fetching before transformation.
 */
export type QueryTransformer = (query: Query) => Query | Promise<Query>;

export interface ThresholdStep {
  value: number | null; // null for base step
  color: string; // hex color
}

export type ThresholdMode = 'absolute' | 'percentage';
export type ThresholdDisplay = 'off' | 'line' | 'dashed-line' | 'area' | 'line+area' | 'dashed-line+area';

export interface ThresholdsConfig {
  enabled: boolean;
  mode: ThresholdMode;
  display: ThresholdDisplay;
  colorSeries: boolean;
  steps: ThresholdStep[];
}

export interface QuerySettings {
  chart: Partial<{
    enable: boolean;
    type: VizType;
    colors: keyof typeof DEFAULT_QUALITATIVE_PALETTES;
    legend: Partial<{
      enable: boolean;
      position: 'bottom' | 'right' | 'left';
      mode: 'table' | 'condensed';
      showRollups: boolean;
      showEventsView: boolean;
    }>;
    xAxis: {
      /** 'UTC', 'Local', or an IANA timezone string like 'America/New_York'. Defaults to 'UTC' */
      timezone?: string;
    };
    yAxis: {
      type: 'linear' | 'logarithmic';
      metric?: string;
    };
    thresholds?: ThresholdsConfig;
  }>;
  // table.enable removed — replaced by legend.enable + legend.mode
}

export default class QueryModel {
  @observable status: QueryStatus = 'init';

  @observable dirty: boolean = true;

  requestId: string = '';

  @observable.ref query: Query;

  @observable.ref settings: QuerySettings;

  results = new UdeQueryResultsCollection({ timestamps: [], rows: [] }, { parent: this });

  @observable.ref rawRows: RawEventRow[] = [];

  @observable.ref rawColumnKeys: string[] = [];

  rawEventsCollection = new RawEventCollection();

  sseClient: Client;

  queryTransformer?: QueryTransformer;

  /**
   * Series interaction state (visibility, hover).
   * Owned by QueryModel because it is the sole shared MobX observable in the UDE.
   * If QueryModel accumulates more UI concerns in the future, consider extracting
   * these fields into a dedicated SeriesInteractionState child model.
   */

  /** Series visibility map: row.id → visible. Absent = visible (default). */
  @observable seriesVisibility: ObservableMap<string, boolean> = observable.map();

  /** ID of the currently hovered series row, or null. */
  @observable hoveredSeriesId: string | null = null;

  /** Row IDs that have a corresponding chart series/point. Set by the chart data view. */
  @observable.ref chartedRowIds: string[] = [];

  /** Derived Set for O(1) membership checks in isRowCharted. */
  private chartedRowIdSet: Set<string> = new Set();

  /**
   * Client-side override for the number of series to display.
   * Used by the chart renderer to slice results without re-running the query.
   */
  @observable vizLimit: number | null = null;

  /** Resolved series limit: vizLimit override → query.viz.limit → 10 */
  @computed get effectiveVizLimit(): number {
    return this.vizLimit ?? this.query.viz?.limit ?? 10;
  }

  constructor(query: Query, settings: QuerySettings, results?: QueryResults, queryTransformer?: QueryTransformer) {
    this.query = query;
    this.settings = settings;
    this.status = 'init';
    this.queryTransformer = queryTransformer;

    if (results) {
      this.dirty = false;
      this.results.replace(results);
      this.rawRows = results.rawRows ?? [];
      this.rawColumnKeys = results.rawColumnKeys ?? [];
      this.rawEventsCollection.replace(this.rawRows);
      this.status = 'done';
    }

    this.sseClient = new Client({
      outType: 'ude/query',
      inType: 'ude/result',
      onSuccess: (data) => {
        const responseId = (data as QueryResults)?.requestID;
        const parentId = (data as QueryResults)?.parentRequestID;
        if (responseId && responseId !== this.requestId && parentId !== this.requestId) {
          return;
        }

        if (this.isLiveUpdate(data)) {
          this.applyLiveUpdate(data);
        } else {
          this.results.replace(data as QueryResults);
          this.rawRows = (data as QueryResults).rawRows ?? [];
          this.rawColumnKeys = (data as QueryResults).rawColumnKeys ?? [];
          this.rawEventsCollection.replace(this.rawRows);
        }
        this.status = 'done';
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (data: any) => {
        if (this.requestId === data.requestID) {
          this.status = 'error';
        }
      },
      onReconnect: () => {
        if (this.status !== 'init') {
          this.results.reset();
          this.run();
        }
      }
    });
  }

  private isLiveUpdate(data: unknown): data is QueryResults {
    return (
      typeof data === 'object' && data !== null && 'liveUpdate' in data && (data as QueryResults).liveUpdate === true
    );
  }

  /**
   * Merge incremental live update data into the existing results and trim
   * timestamps that fall outside the sliding window.
   */
  @action
  private applyLiveUpdate(update: QueryResults) {
    const { timestamps: newTimestamps, rows: newRows, windowSize = 0 } = update;
    const prev = this.results.serialize();

    const tsIndexMap = new Map<number, { source: 'old' | 'new'; idx: number }>();
    for (let i = 0; i < prev.timestamps.length; i++) {
      tsIndexMap.set(prev.timestamps[i], { source: 'old', idx: i });
    }
    for (let i = 0; i < newTimestamps.length; i++) {
      tsIndexMap.set(newTimestamps[i], { source: 'new', idx: i });
    }

    const allTs = Array.from(tsIndexMap.keys()).sort((a, b) => a - b);

    const latestTimestamp = allTs[allTs.length - 1] ?? 0;
    const cutoff = latestTimestamp - windowSize;
    const trimmedTimestamps = allTs.filter((ts) => ts >= cutoff);

    const sources = trimmedTimestamps.map((ts) => tsIndexMap.get(ts) as { source: 'old' | 'new'; idx: number });

    // build a map of existing rows by id for fast lookup
    const rowMap = new Map(prev.rows.map((row) => [row.id, row]));

    // merge each incoming row
    for (const newRow of newRows) {
      const existing = rowMap.get(newRow.id);
      if (existing) {
        const mergedValues: typeof existing.values = {};
        for (const metric of Object.keys(existing.values)) {
          const existingVals = existing.values[metric].values;
          const incomingVals = newRow.values[metric]?.values ?? [];
          const values = sources.map((s) =>
            s.source === 'old' ? (existingVals[s.idx] ?? null) : (incomingVals[s.idx] ?? null)
          );
          mergedValues[metric] = { values };
        }
        for (const metric of Object.keys(newRow.values)) {
          if (!mergedValues[metric]) {
            const incomingVals = newRow.values[metric].values;
            const values = sources.map((s) => (s.source === 'new' ? (incomingVals[s.idx] ?? null) : null));
            mergedValues[metric] = { values };
          }
        }
        rowMap.set(newRow.id, {
          ...existing,
          values: mergedValues,
          rollups: newRow.rollups,
          dimensions: newRow.dimensions
        });
      } else {
        const paddedValues: typeof newRow.values = {};
        for (const metric of Object.keys(newRow.values)) {
          const incomingVals = newRow.values[metric].values;
          const values = sources.map((s) => (s.source === 'new' ? (incomingVals[s.idx] ?? null) : null));
          paddedValues[metric] = { values };
        }
        rowMap.set(newRow.id, { ...newRow, values: paddedValues });
      }
    }
    // If user has an active selection (any entry in the visibility map),
    // default new rows to hidden so they don't break a solo/selection.
    if (this.seriesVisibility.size > 0) {
      for (const newRow of newRows) {
        const isNewRow = !prev.rows.some((r) => r.id === newRow.id);
        if (isNewRow && !this.seriesVisibility.has(newRow.id)) {
          this.seriesVisibility.set(newRow.id, false);
        }
      }
    }

    // Live updates accumulate rows across batches (rowMap is seeded from the previous
    // results and merged with each incoming batch), so the merged set can grow past
    // viz.limit even though the server caps each individual response. Re-apply the cap
    // here, selecting which series survive using the same ordering the server applies,
    // so we strip the least-relevant series rather than the most recently added ones.
    const allRows = Array.from(rowMap.values());
    const limit = this.query.viz?.limit ?? DEFAULT_VIZ_LIMIT;
    const keepIds = new Set(
      sortResultRows(this.query, allRows)
        .slice(0, limit)
        .map((row) => row.id)
    );

    this.results.update({
      timestamps: trimmedTimestamps,
      rows: allRows.map((row) => (keepIds.has(row.id) ? row : { ...row, values: {} }))
    });
  }

  get hasRun() {
    return this.status !== 'init';
  }

  get isLoading() {
    return this.status === 'running' || (this.status === 'partial' && this.results.rows.length === 0);
  }

  get hasError() {
    return this.status === 'error';
  }

  get hasResults() {
    return (this.status === 'partial' || this.status === 'done') && this.results.rows.length > 0;
  }

  get fullyLoaded() {
    return this.status === 'done';
  }

  /**
   * The effective metric name driving the chart y-axis.
   * The stored `yAxis.metric` value can be:
   * - A rollup key like "avg_bits/s" → extracts metric name "bits/s"
   * - Empty → falls back to `query.metrics[0].name`
   */
  @computed get primaryMetric(): string {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it's a rollup key (fn_metricName) matching a configured rollup
      const matchingRollup = this.query.rollups.find((r) => getRollupKey(r) === selected);
      if (matchingRollup) {
        return matchingRollup.metric;
      }

      // Check if it's a plain metric name (backwards compat)
      const isRegularMetric = this.query.metrics.some((m) => m.name === selected);
      if (isRegularMetric) {
        return selected;
      }
    }

    return this.query.metrics[0]?.name ?? '';
  }

  /**
   * The effective rollup key for aggregate chart types.
   * Returns the full rollup key (e.g. "avg_bits/s") or falls back to the first rollup.
   */
  @computed get primaryRollupKey(): string {
    const selected = this.settings.chart.yAxis?.metric;
    if (selected) {
      // Check if it matches a configured rollup
      const matchingRollup = this.query.rollups.find((r) => r.metric === selected);
      if (matchingRollup) {
        return getRollupKey(matchingRollup);
      }
    }
    // Fallback: first rollup key, or plain metric name
    const firstRollup = this.query.rollups[0];
    if (firstRollup) {
      return getRollupKey(firstRollup);
    }

    return this.query.metrics[0]?.name ?? '';
  }

  /** Toggle visibility for a single series row.
   *  Deletes the map entry when restoring to visible (absent = visible default),
   *  so seriesVisibility.size accurately signals "active selection" for live updates. */
  @action toggleSeriesVisibility(rowId: string) {
    const current = this.seriesVisibility.get(rowId);
    if (current === false) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Set visibility for a specific series row (used by cmd+click and shift+click).
   *  Deletes the map entry when setting visible, so only hidden rows occupy the map. */
  @action setSeriesVisibility(rowId: string, visible: boolean) {
    if (visible) {
      this.seriesVisibility.delete(rowId);
    } else {
      this.seriesVisibility.set(rowId, false);
    }
  }

  /** Show all series (reset visibility map). */
  @action showAllSeries() {
    this.seriesVisibility.clear();
  }

  /** Check if a series is visible (absent from map = visible). */
  isSeriesVisible(rowId: string): boolean {
    return this.seriesVisibility.get(rowId) !== false;
  }

  @computed get seriesColors(): Map<string, string> {
    const colors = DEFAULT_QUALITATIVE_PALETTES[this.settings.chart.colors ?? 'default'];
    const seriesColors = new Map<string, string>();
    let colorIndex = 0;
    this.results.rows.forEach((row) => {
      if (this.isRowCharted(row.id)) {
        seriesColors.set(row.id, colors[colorIndex % colors.length]);
        colorIndex += 1;
      }
    });
    return seriesColors;
  }

  getSeriesColor(rowId: string): string | undefined {
    return this.seriesColors.get(rowId);
  }

  @action setHoveredSeries(rowId: string | null) {
    this.hoveredSeriesId = rowId;
  }

  @action setChartedRowIds(ids: string[]) {
    this.chartedRowIds = ids.slice();
    this.chartedRowIdSet = new Set(ids);
  }

  /** Check if a row has a corresponding chart series/point.
   *  When chartedRowIds is empty (chart hasn't reported yet), treat all rows as charted
   *  so the legend doesn't flash in a grayed-out state on initial load.
   *  Reads the @observable.ref chartedRowIds for the empty check so MobX tracks
   *  this dependency and re-renders observers when the chart reports fresh IDs. */
  isRowCharted(rowId: string): boolean {
    return this.chartedRowIds.length === 0 || this.chartedRowIdSet.has(rowId);
  }

  @action async run(queryOverride?: Partial<Query>) {
    const previousRequestId = this.requestId;
    const requestID = uuid.v4();
    this.status = 'running';
    this.requestId = requestID;
    this.dirty = false;

    // Reset series interaction state — user is fetching new data.
    // Note: chartedRowIds is NOT reset here — let it keep stale values until
    // the chart reports fresh ones. Stale IDs won't match new row IDs, so they'll
    // correctly show as "not charted." Resetting to [] would cause a flash where
    // the "empty = all charted" fallback makes everything look charted momentarily.
    this.seriesVisibility.clear();
    this.hoveredSeriesId = null;

    try {
      // set the chart type on the query, and apply any overrides
      let query: Query = merge(
        {
          viz: { type: this.settings.chart.type }
        },
        this.query,
        queryOverride || {}
      );

      // TODO I think we have another way of forcing a specific rollup function in UDE? But I can't track it down again
      if (query.viz?.type === 'timelineView') {
        query.rollups.forEach((rollup) => {
          rollup.fn = 'min';
        });
      }

      // Apply any query transformations (e.g., filter transforms for agent/test name resolution)
      if (this.queryTransformer) {
        query = await this.queryTransformer(query);
      }

      if (previousRequestId && previousRequestId !== requestID) {
        this.sseClient.send({ action: 'RUN_UNSUBSCRIBE', requestID: previousRequestId });
      }

      this.sseClient.send({ action: 'RUN_QUERY', query, requestID });
    } catch (_e) {
      if (this.requestId === requestID) {
        this.status = 'error';
      }
    }
  }

  cleanup() {
    this.sseClient.send({ action: 'RUN_UNSUBSCRIBE', requestID: this.requestId });
    this.sseClient.cleanup();
  }
}
